import { Badge, BodyText } from "@/components/ui";
import type { RxHistoryMedication } from "@/features/Patient/MedicalRecords/RxHistory/types";
import { formatDate } from "@/lib/time";

interface RxHistoryMedicationCardProps {
  medication: RxHistoryMedication;
}

export const RxHistoryMedicationCard = ({
  medication,
}: RxHistoryMedicationCardProps) => {
  const { prescriptionDetails, payer } = medication;
  const {
    displayName,
    strength,
    doseForm,
    route,
    directions,
    quantity,
    refills,
    daysSupply,
    lastFillDate,
    writtenDate,
    otc,
    drugClassification,
  } = prescriptionDetails;

  const payerName = payer.payerName;
  const dateLabel = lastFillDate ? "Filled" : writtenDate ? "Written" : null;
  const dateValue = lastFillDate ?? writtenDate;

  const subtitleParts = [doseForm, route, strength].filter(Boolean);

  return (
    <div
      className="rounded-lg border p-3 flex flex-col gap-1.5"
      data-testid="rx-history-medication-card"
    >
      {/* Name row — flush left, the visual anchor for copy-paste */}
      <div className="flex items-start justify-between gap-2">
        <BodyText size="sm" className="font-medium">
          {displayName}
        </BodyText>
        <div className="flex items-center gap-1 flex-shrink-0">
          {otc && (
            <Badge variant="light" className="text-xs">
              OTC
            </Badge>
          )}
          {drugClassification && (
            <Badge variant="default" className="text-xs">
              {drugClassification}
            </Badge>
          )}
        </div>
      </div>

      {/* Details — indented under the name */}
      <div className="pl-4 flex flex-col gap-1">
        {subtitleParts.length > 0 && (
          <BodyText size="xs" textColor="secondary">
            {subtitleParts.join(" · ")}
          </BodyText>
        )}
        {directions && <BodyText size="sm">{directions}</BodyText>}
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {quantity && <span>Qty: {quantity}</span>}
          {refills && <span>Refills: {refills}</span>}
          {daysSupply > 0 && <span>Days: {daysSupply}</span>}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {dateLabel && dateValue && (
            <span>
              {dateLabel}: {formatDate(dateValue)}
            </span>
          )}
          {payerName && <span>{payerName}</span>}
        </div>
      </div>
    </div>
  );
};
